'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Message, Agent } from '@/types';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { AgentSuggestionsList } from './AgentSuggestionCard';
import { MessageCircle, X, Minimize2, Maximize2, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils/cn';

interface ChatWidgetProps {
  sessionId?: string;
  initialMessage?: string;
  onSessionCreate?: (sessionId: string) => void;
  onBookingRequest?: () => void;
  className?: string;
  embedded?: boolean;
  defaultOpen?: boolean;
  startWithVoice?: boolean;
}

export function ChatWidget({
  sessionId: initialSessionId,
  initialMessage,
  onSessionCreate,
  onBookingRequest,
  className,
  embedded = false,
  defaultOpen = false,
  startWithVoice = false,
}: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(embedded || defaultOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [messages, setMessages] = useState<Message[]>([]);
  const [suggestedAgents, setSuggestedAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(startWithVoice);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const voiceEnabledRef = useRef(voiceEnabled);
  const isRecordingRef = useRef(isRecording);
  const sessionIdRef = useRef(sessionId); // Ref for sessionId to avoid closure issues
  const isInitializingRef = useRef(false); // Guard against double initialization

  // Keep refs in sync with state
  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
  }, [voiceEnabled]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Initialize session (with guard against double execution)
  useEffect(() => {
    if (!sessionId && isOpen && !isInitializingRef.current) {
      isInitializingRef.current = true;
      initializeSession();
    }
  }, [sessionId, isOpen]);

  const initializeSession = async () => {
    try {
      // Get UTM params from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get('utm_source') || undefined,
        utm_medium: urlParams.get('utm_medium') || undefined,
        utm_campaign: urlParams.get('utm_campaign') || undefined,
      };

      const response = await fetch('/api/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: document.referrer ? 'referral' : 'direct',
          utm_params: utmParams,
        }),
      });

      const data = await response.json();

      if (data.session) {
        setSessionId(data.session.id);
        sessionIdRef.current = data.session.id; // Update ref immediately
        onSessionCreate?.(data.session.id);

        // Send initial welcome message
        await sendMessage(initialMessage || '', false, data.session.id, true);
      }
    } catch (error) {
      console.error('Error initializing session:', error);
    }
  };

  const sendMessage = async (
    content: string,
    voiceUsed = false,
    currentSessionId?: string,
    isInitial = false
  ) => {
    // Use ref to get current sessionId (avoids closure issues)
    const activeSessionId = currentSessionId || sessionIdRef.current;
    console.warn('sendMessage called, sessionId:', activeSessionId);
    if (!activeSessionId) {
      console.error('!!! NO SESSION ID - ABORTING !!!');
      return;
    }

    // Add user message to UI (skip for initial request)
    if (!isInitial && content) {
      const userMessage: Message = {
        id: Date.now().toString(),
        session_id: activeSessionId,
        role: 'user',
        content,
        created_at: new Date().toISOString(),
        metadata: { voice_used: voiceUsed },
      };
      setMessages((prev) => [...prev, userMessage]);
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: activeSessionId,
          message: isInitial ? '' : content,
          voiceUsed,
        }),
      });

      const data = await response.json();
      console.warn('=== API RESPONSE ===', data);

      if (data.message) {
        console.warn('Got message from API:', data.message.substring(0, 50) + '...');
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          session_id: activeSessionId,
          role: 'assistant',
          content: data.message,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Play TTS if voice is enabled (use ref to avoid closure issues)
        console.warn('voiceEnabledRef.current:', voiceEnabledRef.current);
        if (voiceEnabledRef.current) {
          console.warn('=== CALLING TTS ===');
          playTTS(data.message);
        } else {
          console.warn('Voice disabled, skipping TTS');
        }
      } else {
        console.warn('No message in API response:', data);
      }

      if (data.suggestedAgents) {
        setSuggestedAgents(data.suggestedAgents);
      }

      if (data.showBooking) {
        setShowBooking(true);
        onBookingRequest?.();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restart listening even if there's an error
      if (voiceEnabledRef.current && !isRecordingRef.current) {
        setTimeout(() => handleStartVoice(), 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Play TTS audio for assistant response
  const playTTS = async (text: string, autoListenAfter = true) => {
    try {
      setIsSpeaking(true);
      console.log('Playing TTS for:', text.substring(0, 50) + '...');

      const response = await fetch('/api/voice/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        console.error('TTS error:', response.status);
        setIsSpeaking(false);
        return;
      }

      const audioBlob = await response.blob();
      console.log('Audio blob received, size:', audioBlob.size);

      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);

          // Auto-start listening after assistant finishes speaking
          if (autoListenAfter && voiceEnabledRef.current && !isRecordingRef.current) {
            console.log('Auto-starting voice recording...');
            setTimeout(() => handleStartVoice(), 500);
          }
        };
        audioRef.current.onerror = (e) => {
          console.error('Audio playback error:', e);
          setIsSpeaking(false);
          // Restart listening even if TTS fails
          if (autoListenAfter && voiceEnabledRef.current && !isRecordingRef.current) {
            setTimeout(() => handleStartVoice(), 500);
          }
        };

        try {
          await audioRef.current.play();
          console.log('Audio playing');
        } catch (playError) {
          console.error('Play error (autoplay blocked?):', playError);
          setIsSpeaking(false);
          // Restart listening even if autoplay is blocked
          if (autoListenAfter && voiceEnabledRef.current && !isRecordingRef.current) {
            setTimeout(() => handleStartVoice(), 500);
          }
        }
      } else {
        console.error('Audio ref not available');
        setIsSpeaking(false);
        // Restart listening even if audio ref is not available
        if (autoListenAfter && voiceEnabledRef.current && !isRecordingRef.current) {
          setTimeout(() => handleStartVoice(), 500);
        }
      }
    } catch (error) {
      console.error('Error playing TTS:', error);
      setIsSpeaking(false);
      // Restart listening even if TTS completely fails
      if (voiceEnabledRef.current && !isRecordingRef.current) {
        setTimeout(() => handleStartVoice(), 500);
      }
    }
  };

  const handleStartVoice = async () => {
    try {
      // Check if mediaDevices is available (requires HTTPS or localhost)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Il microfono richiede una connessione HTTPS. La funzione voce non è disponibile su HTTP.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      // Setup Voice Activity Detection (VAD)
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.1;
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      let silenceStart: number | null = null;
      const SILENCE_THRESHOLD = 15; // Audio level threshold (0-255)
      const SILENCE_DURATION = 1500; // Stop after 1.5 seconds of silence
      const MIN_RECORDING_TIME = 500; // Minimum recording time before checking silence
      const recordingStartTime = Date.now();
      let vadInterval: NodeJS.Timeout | null = null;

      // Check audio levels periodically
      vadInterval = setInterval(() => {
        if (!isRecordingRef.current) {
          if (vadInterval) clearInterval(vadInterval);
          return;
        }

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;

        const recordingTime = Date.now() - recordingStartTime;

        if (recordingTime > MIN_RECORDING_TIME) {
          if (average < SILENCE_THRESHOLD) {
            // Silence detected
            if (silenceStart === null) {
              silenceStart = Date.now();
            } else if (Date.now() - silenceStart > SILENCE_DURATION) {
              // Silence for too long, stop recording
              console.log('=== VAD: SILENCE DETECTED, STOPPING ===');
              if (vadInterval) clearInterval(vadInterval);
              audioContext.close();
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                console.log('Calling mediaRecorder.stop()...');
                mediaRecorderRef.current.stop();
                setIsRecording(false);
                isRecordingRef.current = false;
              } else {
                console.log('MediaRecorder not in recording state:', mediaRecorderRef.current?.state);
              }
            }
          } else {
            // Sound detected, reset silence timer
            silenceStart = null;
          }
        }
      }, 100);

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());

        // DEBUG: Very visible log
        console.log('=== RECORDING STOPPED ===');
        console.log('Chunks:', audioChunksRef.current.length);

        // Check if we have audio data
        if (audioChunksRef.current.length === 0) {
          console.log('No audio chunks recorded');
          if (voiceEnabledRef.current && !isRecordingRef.current) {
            setTimeout(() => handleStartVoice(), 500);
          }
          return;
        }

        // Process audio
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('Audio blob size:', audioBlob.size);

        // Skip if audio is too small (likely just noise)
        if (audioBlob.size < 1000) {
          console.log('Audio too small, skipping');
          if (voiceEnabledRef.current && !isRecordingRef.current) {
            setTimeout(() => handleStartVoice(), 500);
          }
          return;
        }

        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');

        try {
          console.log('Sending audio to STT...');
          const response = await fetch('/api/voice/stt', {
            method: 'POST',
            body: formData,
          });

          console.warn('=== STT RESPONSE ===');
          const data = await response.json();
          console.warn('Transcript:', data.transcript);

          if (data.transcript && data.transcript.trim().length > 1) {
            const transcript = data.transcript.trim();

            // Simple noise filter - only filter obvious noise patterns
            const isNoise = /^\[.*\]$/i.test(transcript) || /^\s*$/.test(transcript);

            if (!isNoise) {
              console.warn('=== SENDING MESSAGE v2 ===', transcript);
              await sendMessage(transcript, true);
              console.warn('=== MESSAGE SENT v2 ===');
            } else {
              console.warn('Filtered as noise:', transcript);
              if (voiceEnabledRef.current) {
                setTimeout(() => handleStartVoice(), 500);
              }
            }
          } else {
            console.warn('No valid transcript, restarting listening');
            if (voiceEnabledRef.current) {
              setTimeout(() => handleStartVoice(), 500);
            }
          }
        } catch (error) {
          console.error('Error processing audio:', error);
          // Restart listening even if there's an error
          if (voiceEnabledRef.current && !isRecordingRef.current) {
            setTimeout(() => handleStartVoice(), 1000);
          }
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms for more reliable recording
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      isRecordingRef.current = true;
    } catch (error) {
      console.error('Error starting voice:', error);
      setIsRecording(false);
    }
  };

  const handleStopVoice = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // When voice mode is toggled ON during conversation (not on initial load)
  // This handles the case when user starts in text mode and later enables voice
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    // Skip on initial mount - the welcome message TTS is handled by sendMessage
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      return;
    }

    // Only trigger when voice is manually enabled during conversation
    if (voiceEnabled && messages.length > 0 && !isSpeaking && !isLoading && !isRecording) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        console.log('Voice mode toggled on, playing last message...');
        playTTS(lastMessage.content, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceEnabled]);

  if (!embedded && !isOpen) {
    return (
      <>
        {/* Hidden audio element must always be mounted for TTS to work */}
        <audio ref={audioRef} className="hidden" />
        <Button
          size="icon"
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg"
          onClick={() => setIsOpen(true)}
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      </>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col bg-white',
        embedded
          ? 'h-full w-full'
          : 'fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] rounded-xl border shadow-2xl',
        isMinimized && !embedded && 'h-14',
        !isMinimized && !embedded && 'h-[600px] max-h-[calc(100vh-48px)]',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-indigo-600 px-4 py-3 text-white rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
            <MessageCircle className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-semibold">Assistente didatticloud</h3>
            {!isMinimized && (
              <p className="text-xs text-indigo-200">Online</p>
            )}
          </div>
        </div>

        <div className="flex gap-1">
          {/* Voice toggle button */}
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              "h-8 w-8 text-white hover:bg-white/20",
              voiceEnabled && "bg-white/20"
            )}
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            title={voiceEnabled ? "Disattiva voce" : "Attiva voce"}
          >
            {voiceEnabled ? (
              <Volume2 className="h-4 w-4" />
            ) : (
              <VolumeX className="h-4 w-4" />
            )}
          </Button>

          {!embedded && (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-white hover:bg-white/20"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? (
                  <Maximize2 className="h-4 w-4" />
                ) : (
                  <Minimize2 className="h-4 w-4" />
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-white hover:bg-white/20"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto">
            {messages.map((message, index) => (
              <ChatMessage
                key={message.id}
                message={message}
                isLatest={index === messages.length - 1}
              />
            ))}

            {isLoading && <TypingIndicator />}

            {suggestedAgents.length > 0 && (
              <AgentSuggestionsList agents={suggestedAgents} />
            )}

            {showBooking && (
              <div className="p-4">
                <Button
                  className="w-full"
                  onClick={onBookingRequest}
                >
                  Prenota un appuntamento
                </Button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <ChatInput
            onSendMessage={(msg, voice) => sendMessage(msg, voice)}
            onStartVoice={handleStartVoice}
            onStopVoice={handleStopVoice}
            isRecording={isRecording}
            isLoading={isLoading || isSpeaking}
          />
        </>
      )}

      {/* Hidden audio element for TTS playback */}
      <audio ref={audioRef} className="hidden" />
    </div>
  );
}
